import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Switch from 'components/Switch';
import {
  TEXT_ACCOUNT_TYPE,
  TEXT_AREA,
  TEXT_BUTTON_CREATE,
  TEXT_BUTTON_UPDATE,
  TEXT_CANCEL,
  TEXT_CHANGE_PASSWORD,
  TEXT_CONFIRM_PASSWORD,
  TEXT_CONTACT,
  TEXT_DESCRIPTION,
  TEXT_GROUP,
  TEXT_LOGIN_ID,
  TEXT_MAXIMIZE_VIDEO,
  TEXT_NAME_LIST,
  TEXT_PASSWORD,
  TEXT_PHONE,
  TEXT_RECORDED_VIDEO,
  TEXT_ROLE_ADMIN,
  TEXT_ROLE_MANAGER,
  TEXT_ROLE_USER,
  TEXT_TWO_FACTOR_AUTHENTICATION,
  TEXT_USER_NAME,
  TEXT_INITCOORD,
  DIALOG_ERROR_GENERAL_ERROR_MESSAGE,
  TEXT_MAP_ZOOM_RATIO
} from "constants/message";
import { useAuth } from 'context/AuthContext';
import { useDialog } from 'context/DialogContext';
import { useGeneralSetting } from 'context/GeneralSettingContext';
import { useSensorGroups } from 'context/SensorGroupsContext';
import { AccountType, SensorType } from 'models';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { USER_PROPERTIES } from "utils/constanst";
import { validateConfirmPassword, validateLoginId, validateRequired } from "utils/validator";
import "./ModifyUserForm.css";
import { standardizeString } from 'utils/function';
import RangeSlider from 'components/RangeSlider';

const validateLatitude = {
  required: '緯度は必須です',
  validate: v => {
    const n = Number(v);
    if (Number.isNaN(n)) return '数値で入力してください';
    if (n < -90 || n > 90) return '緯度は -90〜90 の範囲で入力してください';
    return true;
  },
};

const validateLongitude = {
  required: '経度は必須です',
  validate: v => {
    const n = Number(v);
    if (Number.isNaN(n)) return '数値で入力してください';
    if (n < -180 || n > 180) return '経度は -180〜180 の範囲で入力してください';
    return true;
  },
};

const FORM_ITEMS = [
  { name: USER_PROPERTIES.NAME, displayName: TEXT_USER_NAME, notNull: true, inputType: 'text' },
  { name: USER_PROPERTIES.EMAIL, displayName: TEXT_LOGIN_ID, notNull: true, inputType: 'text' },
  { name: USER_PROPERTIES.PASSWORD, displayName: TEXT_PASSWORD, notNull: true, inputType: 'password' },
  { name: USER_PROPERTIES.CONFIRM_PASSWORD, displayName: TEXT_CONFIRM_PASSWORD, notNull: true, inputType: 'password' },
  { name: USER_PROPERTIES.ACCOUNT_TYPE, displayName: TEXT_ACCOUNT_TYPE, notNull: true, inputType: 'select' },
  { name: USER_PROPERTIES.CONTACT, displayName: TEXT_CONTACT, notNull: false, inputType: 'text' },
  { name: USER_PROPERTIES.PHONE, displayName: TEXT_PHONE, notNull: false, inputType: 'text' },
  { name: USER_PROPERTIES.ENABLED_MFA, displayName: TEXT_TWO_FACTOR_AUTHENTICATION, notNull: false, inputType: 'checkbox' },

  { name: USER_PROPERTIES.GROUP, displayName: TEXT_GROUP, notNull: false, inputType: 'checkboxes' },
  { name: USER_PROPERTIES.NAME_LIST, displayName: TEXT_NAME_LIST, notNull: false, inputType: 'multicustom' },
  { name: USER_PROPERTIES.MAXIMIZE_VIDEO, displayName: TEXT_MAXIMIZE_VIDEO, notNull: false, inputType: 'checkbox', dependsOn: [USER_PROPERTIES.GROUP] },
  { name: USER_PROPERTIES.RECORDED_VIDEO, displayName: TEXT_RECORDED_VIDEO, notNull: false, inputType: 'checkbox', dependsOn: [USER_PROPERTIES.GROUP] },
  { name: USER_PROPERTIES.AREA, displayName: TEXT_AREA, notNull: false, inputType: 'select', dependsOn: [USER_PROPERTIES.GROUP] },
  { name: USER_PROPERTIES.INITCOORD, displayName: TEXT_INITCOORD, notNull: false, inputType: 'latlong', dependsOn: [USER_PROPERTIES.GROUP] },
  { name: USER_PROPERTIES.MAP_ZOOM_RATIO, displayName: TEXT_MAP_ZOOM_RATIO, notNull: false, inputType: 'slider', min: 0, max: 20, step: 0.25 },

  { name: USER_PROPERTIES.DESCRIPTION, displayName: TEXT_DESCRIPTION, notNull: false, inputType: 'text' },
];

// ------- COMPONENT -------
const propTypes = {
  user: PropTypes.object,
  onCancel: PropTypes.func,
  onModifyUser: PropTypes.func,
  onChangePassword: PropTypes.func,
};
const defaultProps = {
  user: {},
  onCancel: () => { },
  onModifyUser: () => { },
  onChangePassword: () => { },
};

const ModifyUserForm = ({ user: initUser, sensors, onCancel, onModifyUser, onChangePassword }) => {
  // ── React-Hook-Form ──
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    setValue,
    reset
  } = useForm();

  const { sensorGroups } = useSensorGroups();
  const { error } = useDialog();
  const { generalSetting, defaultBounding } = useGeneralSetting();
  const { currentUser } = useAuth();
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const [nameSearchTerm, setNameSearchTerm] = useState('');
  const nameDropdownRef = useRef(null);
  const nameSearchInputRef = useRef(null);
  const selectedGroups = watch(USER_PROPERTIES.GROUP, initUser.data?.group || []);

  useEffect(() => {
    reset({
      ...initUser,
      enabledMFA: initUser.enabledMFA || false,
      data: {
        ...initUser.data,
        group: initUser.data?.group || [],
        nameList: initUser.data?.nameList || [],
        maximizeVideo: initUser.data?.maximizeVideo ?? true,
        recordedVideo: initUser.data?.recordedVideo ?? true,
        area: initUser.data?.area || '',
        latitude: initUser.data?.latitude || '',
        longitude: initUser.data?.longitude || '',
        mapZoomRatio: initUser.data?.mapZoomRatio || defaultBounding?.zoom
      }
    })
  }, [initUser, reset]);

  useEffect(() => {
    const handler = (e) => {
      if (nameDropdownRef.current && !nameDropdownRef.current.contains(e.target)) {
        setNameDropdownOpen(false);
        setNameSearchTerm(''); // Clear search when closing dropdown
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (nameDropdownOpen && nameSearchInputRef.current) {
      setTimeout(() => {
        nameSearchInputRef.current?.focus();
      }, 100);
    }
  }, [nameDropdownOpen]);

  // ── Constants ──
  const CAMERA_GROUP_ID = useMemo(() =>
    sensorGroups?.find(g => g.type === SensorType.CAMERA)?.id || '',
    [sensorGroups]
  );

  const NAME_OPTIONS = useMemo(() => {
    if (!sensors?.length || !selectedGroups?.length) return [];
    return sensors
      .filter(s => s.groupId && selectedGroups.includes(s.groupId))
      .map(s => ({ value: s.id, label: `${s.managementId} ${s.name}` }));
  }, [sensors, selectedGroups]);

  const FILTERED_NAME_OPTIONS = useMemo(() => {
    if (!nameSearchTerm.trim()) {
      return NAME_OPTIONS;
    }
    let keywords = standardizeString(nameSearchTerm).replaceAll(/[、､ 　]/g, ",");
    keywords = keywords.split(",");
    keywords = keywords.map(keyword => keyword.trim());
    keywords = keywords.filter(keyword => keyword !== "");

    return NAME_OPTIONS.filter(option => {
      const sensor = sensors.find(s => s.id === option.value);
      if (!sensor) return false;
      const sensorInfo = standardizeString(sensor.managementId) + ' ' + standardizeString(sensor.name);
      return keywords.some(kw => sensorInfo.includes(kw));
    });
  }, [NAME_OPTIONS, nameSearchTerm, sensors]);

  const ROLE_OPTIONS = useMemo(() => {
    const roles = [AccountType.USER, AccountType.MANAGER];
    if (currentUser.accountType === AccountType.ADMIN) {
      roles.push(AccountType.ADMIN);
    }
    return roles.map(role => ({ value: role, label: role === AccountType.USER ? TEXT_ROLE_USER : role === AccountType.MANAGER ? TEXT_ROLE_MANAGER : TEXT_ROLE_ADMIN }));
  }, [currentUser.accountType]);

  const AREA_OPTIONS = useMemo(() => {
    return generalSetting?.borderSettings?.map(area => {
      const fullDisplayName = area?.location?.displayName || '';
      const cityName = fullDisplayName.split(',')[0];
      return {
        value: area.id,
        label: cityName,
      };
    }) || [];
  }, [generalSetting]);

  const listCameraActiveIds = useMemo(() => {
    return sensors.filter(s => s.groupId === CAMERA_GROUP_ID && s.data?.cameraFeature).map(s => s.id) || [];
  }, [sensors, CAMERA_GROUP_ID]);

  // ── Helpers ──
  const shouldShowItem = (item) => {
    if (!item.dependsOn) return true;
    return item.dependsOn.every((dep) => {
      if (dep === USER_PROPERTIES.GROUP) {
        return (selectedGroups || []).includes(CAMERA_GROUP_ID);
      }
      return true;
    });
  };

  const handleNameDropdownToggle = useCallback(() => {
    setNameDropdownOpen(prev => !prev);
  }, []);

  const handleSearchTermChange = useCallback((e) => {
    setNameSearchTerm(e.target.value);
  }, []);

  const toggleNameOption = useCallback((value, list) => {
    const freshList = (list || []).filter(id => sensors.find(s => s.id === id));
    const updated = freshList.includes(value)
      ? freshList.filter(v => v !== value)
      : [...freshList, value];
    setValue(USER_PROPERTIES.NAME_LIST, updated);
  }, [sensors, setValue]);

  const handleGroupChange = useCallback((field, g) => {
    const currentGroups = field.value || [];
    const isChecking = !currentGroups.includes(g.id);
    const newGroups = isChecking
      ? [...currentGroups, g.id]
      : currentGroups.filter(v => v !== g.id);

    field.onChange(newGroups);

    if (!isChecking) {
      const updatedNameList = (watch(USER_PROPERTIES.NAME_LIST) || [])
        .filter(id => !sensors.find(s => s.groupId === g.id && s.id === id));
      setValue(USER_PROPERTIES.NAME_LIST, updatedNameList);
    }

    if (isChecking && g.id === CAMERA_GROUP_ID) {
      setValue(USER_PROPERTIES.MAXIMIZE_VIDEO, true);
      setValue(USER_PROPERTIES.RECORDED_VIDEO, true);
    }
  }, [watch, setValue, sensors, CAMERA_GROUP_ID]);

  // ── Submit ──
  const handleModify = useCallback(async (data) => {
    try {
      if (initUser.email) data.email = initUser.email;

      const selectedArea = AREA_OPTIONS.find(option =>
        String(option.value) === data.data?.area
      );
      if (selectedArea) {
        data.data.labelArea = selectedArea.label;
      }

      const deviceDirections = sensors
        .filter(s => data.data?.nameList?.includes(s.id))
        .map(s => ({
          id: s.id,
          directions: [s.data?.camera1Direction, s.data?.camera2Direction].filter(Boolean)
        }));

      await onModifyUser(data, deviceDirections);
    } catch (e) {
      error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE)
    }
  }, [initUser.email, AREA_OPTIONS, sensors, onModifyUser, error]);

  const renderSelectField = useCallback((item) => (
    <select className="modify_user_form_select" {...register(item.name)}>
      {(item.name === USER_PROPERTIES.AREA ? AREA_OPTIONS : ROLE_OPTIONS).map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  ), [register, AREA_OPTIONS, ROLE_OPTIONS]);

  const renderCheckboxGroup = useCallback((item) => (
    <Controller
      control={control}
      name={item.name}
      render={({ field }) => (
        <div className="modify_user_form_checkbox_group">
          {sensorGroups?.map((g) => (
            <label key={g.id} className="modify_user_form_checkbox_item">
              <input
                type="checkbox"
                checked={(field.value || []).includes(g.id)}
                onChange={() => handleGroupChange(field, g)}
              />
              {g.name}
            </label>
          ))}
        </div>
      )}
    />
  ), [control, sensorGroups, handleGroupChange]);

  // ── RENDER ──
  return (
    <form className="modify_user_form_container" onSubmit={handleSubmit(handleModify)}>
      {FORM_ITEMS.map((item) => {
        if (initUser.id && (item.name === USER_PROPERTIES.PASSWORD || item.name === USER_PROPERTIES.CONFIRM_PASSWORD)) return null; // hide pwd fields on edit
        if (!shouldShowItem(item)) return null;

        return (
          <React.Fragment key={item.name}>
            <div className="modify_user_form_item_row" style={{ flexDirection: item.inputType === 'checkbox' && 'row', alignItems: item.name === USER_PROPERTIES.GROUP ? 'flex-start' : undefined, height: item.name === USER_PROPERTIES.GROUP ? 'auto' : undefined }}>
              {/* Key */}
              <div className="modify_user_form_item_key">
                {item.displayName}
                {item.notNull && <label className="modify_user_form_item_notnull">*</label>}
              </div>

              {/* Value */}
              <div className={`modify_user_form_item_value ${errors[item.name] ? 'error' : ''}`}>

                {/* SELECT */}
                {item.inputType === 'select' && renderSelectField(item)}

                {/* CHECKBOX GROUP */}
                {item.inputType === 'checkboxes' && renderCheckboxGroup(item)}

                {/* SINGLE CHECKBOX / SWITCH */}
                {item.inputType === 'checkbox' && (
                  <Controller control={control} name={item.name} render={({ field }) => <Switch {...field} />} />
                )}

                {/* MULTI-CUSTOM CHECKBOX DROPDOWN */}
                {item.inputType === 'multicustom' && (
                  <div style={{ position: 'relative' }} ref={nameDropdownRef}>
                    {/* Trigger */}
                    <div
                      onClick={handleNameDropdownToggle}
                      className="modify_user_form_select multicustom-dropdown"
                    >
                      <label>
                        <input
                          type="checkbox"
                          checked={NAME_OPTIONS.every((opt) => (watch(USER_PROPERTIES.NAME_LIST) || []).includes(opt.value))}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            const updated = checked ? [...new Set([...NAME_OPTIONS.map(opt => opt.value)])] : [];
                            setValue(USER_PROPERTIES.NAME_LIST, updated);
                          }}
                        />
                        すべて
                      </label>
                      <span className="dropdown-arrow">
                        <FontAwesomeIcon icon={faChevronDown} />
                      </span>
                    </div>
                    {/* Dropdown */}
                    {nameDropdownOpen && (
                      <div className="modify_user_form_select_dropdown">
                        {/* Search Input */}
                        <div className="search-input-container">
                          <input
                            ref={nameSearchInputRef}
                            type="text"
                            placeholder="センサー名または管理IDで検索..."
                            value={nameSearchTerm}
                            onChange={handleSearchTermChange}
                            className="search-input"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="dropdown-content">
                          <label>
                            <input
                              type="checkbox"
                              checked={NAME_OPTIONS.every((opt) => (watch(USER_PROPERTIES.NAME_LIST) || []).includes(opt.value))}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const updated = checked ? [...new Set([...NAME_OPTIONS.map(opt => opt.value)])] : [];
                                setValue(USER_PROPERTIES.NAME_LIST, updated);
                              }}
                            />
                            すべて
                          </label>
                          {selectedGroups.includes(CAMERA_GROUP_ID) && <label style={{ display: 'block' }}>
                            <input
                              type="checkbox"
                              checked={listCameraActiveIds.every((id) => (watch(USER_PROPERTIES.NAME_LIST) || []).includes(id))}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const current = watch(USER_PROPERTIES.NAME_LIST, []).filter(id => sensors.find(s => s.id === id));
                                const updated = checked
                                  ? Array.from(new Set([...current, ...listCameraActiveIds]))
                                  : current.filter(id => !listCameraActiveIds.includes(id));
                                setValue(USER_PROPERTIES.NAME_LIST, updated);
                              }}
                            />
                            カメラ機能ON
                          </label>}

                          {/* Filtered Options */}
                          {FILTERED_NAME_OPTIONS.length === 0 && nameSearchTerm ? (
                            <div className="no-results">検索結果がありません</div>
                          ) : (
                            FILTERED_NAME_OPTIONS.map((opt) => (
                              <label key={opt.value} style={{ display: 'block' }}>
                                <input
                                  type="checkbox"
                                  checked={(watch(USER_PROPERTIES.NAME_LIST) || []).includes(opt.value)}
                                  onChange={() => toggleNameOption(opt.value, watch(USER_PROPERTIES.NAME_LIST) || [])}
                                />
                                {opt.label}
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* NEW LAT-LONG INLINE */}
                {item.inputType === 'latlong' && (
                  <div className="latlong_container">
                    <label className="latlong_label">
                      緯度
                      <input
                        type="text"
                        style={{ width: 140, marginLeft: 4 }}
                        {...register(USER_PROPERTIES.LATITUDE, validateLatitude)}
                      />
                      {errors.data?.latitude && <p className="error_msg">{errors.data.latitude.message}</p>}
                    </label>
                    <label className="latlong_label">
                      経度
                      <input
                        type="text"
                        style={{ width: 140, marginLeft: 4 }}
                        {...register(USER_PROPERTIES.LONGITUDE, validateLongitude)}
                      />
                      {errors.data?.longitude && <p className="error_msg">{errors.data.longitude.message}</p>}
                    </label>
                  </div>
                )}

                {/* DEFAULT INPUT */}
                {['text', 'password'].includes(item.inputType) && (
                  <input
                    autoComplete="new-password"
                    type={item.inputType}
                    disabled={item.name === 'email' && initUser.id}
                    {...(
                      (item.name === USER_PROPERTIES.EMAIL && register(item.name, initUser.id ? { required: false } : validateLoginId)) ||
                      (item.name === USER_PROPERTIES.CONFIRM_PASSWORD && register(item.name, validateConfirmPassword(watch(USER_PROPERTIES.PASSWORD)))) ||
                      (item.notNull && register(item.name, validateRequired(item.displayName))) ||
                      register(item.name)
                    )}
                  />
                )}

                {/* SLIDER */}
                { item.inputType === 'slider' && (
                  <div className="slider_container">
                    <span className="slider_value">{watch(item.name) ?? item.min}</span>
                    <RangeSlider
                      min={item.min}
                      max={item.max}
                      step={item.step}
                      progress={((watch(item.name) ?? item.min) - item.min) / (item.max - item.min)}
                      value={watch(item.name)}
                      displayMinMax={false}
                      {...register(item.name)}
                    />
                  </div>
                  )
                }
              </div>
            </div>
            {errors[item.name] && (
              <label className="modify_user_form_error">{errors[item.name].message}</label>
            )}
          </React.Fragment>
        );
      })}

      {/* BUTTONS */}
      <div className="modify_user_form_buttons_container">
        <button className="modify_user_form_button button_active">
          {initUser.id ? TEXT_BUTTON_UPDATE : TEXT_BUTTON_CREATE}
        </button>
        {initUser.id && (
          <button type="button" className="modify_user_form_button button_active" onClick={onChangePassword}>
            {TEXT_CHANGE_PASSWORD}
          </button>
        )}
        <button className="button_cancel" onClick={onCancel}>
          {TEXT_CANCEL}
        </button>
      </div>
    </form>
  );
};

ModifyUserForm.propTypes = propTypes;
ModifyUserForm.defaultProps = defaultProps;

export default ModifyUserForm;
